import React, { useRef, useState } from "react";

import { useDevice } from "../../../../Api/newSockets.api";
import DeviceMenu from "../../Shared/DeviceMenu";
import DevicesHeader from "../../../../components/Header/DevicesHeader";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import { savePathToLocalStorage } from "../../../../utils/helpers";

type Props = {
  match: any;
  deviceId: string;
  label: string;
  icon: any;
};

const ACCENT = "#673ab7";
const SCAN = "#00e5ff";

type DiagImages = {
  color?: string | null;
  depth?: string | null;
  overlay?: string | null;
  object?: string | null;
};

const IMG_INFO: { key: keyof DiagImages; title: string; desc: string }[] = [
  { key: "color", title: "Color", desc: "Raw RGB frame from the RealSense" },
  { key: "depth", title: "Depth", desc: "Colorized depth (blue = near, red = far)" },
  { key: "overlay", title: "Segmentation", desc: "Green = chosen object · Red = other off-plane regions" },
  { key: "object", title: "Isolated object", desc: "What gets turned into the object point cloud" },
];

const Diagnostics = (props: Props) => {
  savePathToLocalStorage();
  const deviceId = props.match.params.deviceId;

  const { getDevice, stereoDiagnose, streamStereoObject } = useDevice();
  const device = getDevice(parseInt(deviceId));
  const deviceData = device.device;

  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState<DiagImages | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // ---- Streaming self-test -------------------------------------------------
  const [streamLog, setStreamLog] = useState<string[]>([]);
  const [streamRunning, setStreamRunning] = useState(false);
  const streamStart = useRef<number>(0);
  const streamAccum = useRef<{ chunks: number; points: number; bytes: number; total: number }>({
    chunks: 0,
    points: 0,
    bytes: 0,
    total: 0,
  });

  const log = (line: string) => {
    const t = ((performance.now() - streamStart.current) / 1000).toFixed(2);
    setStreamLog((prev) => [...prev, `[+${t}s] ${line}`]);
    // Also mirror to console for deep inspection.
    // eslint-disable-next-line no-console
    console.log("[stereo-stream-diag]", line);
  };

  const runStreamTest = () => {
    if (streamRunning) return;
    setStreamRunning(true);
    setStreamLog([]);
    streamStart.current = performance.now();
    streamAccum.current = { chunks: 0, points: 0, bytes: 0, total: 0 };
    log(`emitting stereoStreamObject for device ${deviceId} …`);

    // Safety timeout so the UI doesn't hang if nothing comes back.
    const watchdog = setTimeout(() => {
      if (streamRunning) {
        log("⚠ no completion after 30s — likely no chunks arriving (relay/socket issue)");
        setStreamRunning(false);
      }
    }, 30000);

    let firstChunkAt = 0;
    const cleanup = streamStereoObject(deviceId, {
      onChunk: (evt: any) => {
        const a = streamAccum.current;
        if (a.chunks === 0) {
          firstChunkAt = performance.now();
          // Inspect the very first chunk in detail.
          const dtype =
            evt?.data == null
              ? "null"
              : evt.data instanceof ArrayBuffer
              ? "ArrayBuffer"
              : ArrayBuffer.isView(evt.data)
              ? `TypedArray(${(evt.data as any).constructor?.name})`
              : typeof evt.data;
          const byteLen =
            evt?.data instanceof ArrayBuffer
              ? evt.data.byteLength
              : ArrayBuffer.isView(evt.data)
              ? (evt.data as any).byteLength
              : evt?.data?.length ?? "?";
          log(`first chunk: total=${evt.totalPoints} totalChunks=${evt.totalChunks} chunkPoints=${evt.chunkPoints} stride=${evt.stride}`);
          log(`  data type=${dtype} byteLength=${byteLen} deviceId=${evt.deviceId}`);
          a.total = evt.totalPoints || 0;
        }
        a.chunks += 1;
        a.points += evt.chunkPoints || 0;
        const bl =
          evt?.data instanceof ArrayBuffer
            ? evt.data.byteLength
            : ArrayBuffer.isView(evt.data)
            ? (evt.data as any).byteLength
            : 0;
        a.bytes += bl;
        if (a.chunks <= 3 || a.chunks % 5 === 0) {
          log(`chunk #${evt.index}: +${evt.chunkPoints}pts (${bl}B) → ${a.points}/${a.total}`);
        }
      },
      onDone: (info: any) => {
        clearTimeout(watchdog);
        const a = streamAccum.current;
        const dur = ((performance.now() - firstChunkAt) / 1000).toFixed(2);
        log(`✓ DONE: ${a.chunks} chunks, ${a.points} points, ${(a.bytes / 1024).toFixed(0)}KB in ${dur}s`);
        if (a.total > 0 && a.points !== a.total) {
          log(`⚠ point mismatch: assembled ${a.points} but total was ${a.total}`);
        }
        if (a.points === 0) {
          log("⚠ ZERO points assembled — chunks arrived but carried no data");
        }
        setStreamRunning(false);
      },
      onError: (err: string) => {
        clearTimeout(watchdog);
        log(`✗ ERROR from server: ${err}`);
        setStreamRunning(false);
      },
    });
    // If the hook couldn't attach (socket not ready), it returns a no-op cleanup.
    void cleanup;
  };

  const runDiagnostics = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const res: any = await stereoDiagnose(deviceId);
      if (res && res.ok) {
        setImages(res.images || {});
        setStats(res.stats || {});
      } else {
        setError(res?.error || "Diagnostics failed");
      }
    } catch (e: any) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  const src = (b64?: string | null) => (b64 ? `data:image/jpeg;base64,${b64}` : undefined);

  return deviceData ? (
    <div className="device-main main-content">
      <DevicesHeader device={deviceData} />
      <div className="stream-body-content">
        <div>
          <DeviceMenu typeId={deviceData.deviceTypes.id} />
        </div>
        <div className="stream-content" style={{ padding: "1rem 1.5rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem" }}>
            <div>
              <h2 style={{ margin: 0, color: ACCENT, letterSpacing: 1 }}>Segmentation Diagnostics</h2>
              <p style={{ margin: "0.25rem 0 0", color: "#8a8a8a", fontSize: "0.85rem", fontFamily: "monospace" }}>
                Capture a frame and inspect exactly what the object isolation selects.
              </p>
            </div>
            <button
              onClick={runDiagnostics}
              disabled={loading}
              style={{
                padding: "0.6rem 1.4rem",
                borderRadius: 8,
                border: `2px solid ${ACCENT}`,
                background: "linear-gradient(135deg, rgba(103,58,183,0.4) 0%, rgba(103,58,183,0.2) 100%)",
                color: ACCENT,
                fontWeight: "bold",
                textTransform: "uppercase",
                cursor: loading ? "wait" : "pointer",
                boxShadow: "0 0 20px rgba(103,58,183,0.35)",
              }}
            >
              {loading ? "Running…" : "▶ Run Diagnostics"}
            </button>
          </div>

          {error && (
            <div style={{ color: "#ff6b6b", fontFamily: "monospace", fontSize: 13, margin: "0.5rem 0 1rem" }}>
              ⚠ {error}
            </div>
          )}

          {/* Stats readout */}
          {stats && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.75rem", margin: "0 0 1rem" }}>
              {[
                ["Object found", stats.has_object ? "yes" : "no"],
                ["Blobs", stats.num_blobs],
                ["Off-plane %", stats.not_plane_pct != null ? `${stats.not_plane_pct}%` : "—"],
                ["Object px", stats.object_pixels?.toLocaleString?.() ?? "—"],
                ["Plane normal", Array.isArray(stats.plane_normal) ? `[${stats.plane_normal.join(", ")}]` : "—"],
              ].map(([k, v]) => (
                <div key={String(k)} style={{ background: "rgba(103,58,183,0.12)", border: "1px solid rgba(103,58,183,0.3)", borderRadius: 8, padding: "0.4rem 0.8rem" }}>
                  <div style={{ color: "#8a8a8a", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>{k}</div>
                  <div style={{ color: ACCENT, fontFamily: "monospace", fontSize: 13, fontWeight: "bold" }}>{String(v)}</div>
                </div>
              ))}
            </div>
          )}

          {/* Image grid */}
          {images ? (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "1rem" }}>
              {IMG_INFO.map(({ key, title, desc }) => (
                <div key={key} style={{ border: `1px solid ${key === "overlay" ? SCAN : "rgba(103,58,183,0.3)"}`, borderRadius: 10, overflow: "hidden", background: "#0d0d0d" }}>
                  <div style={{ padding: "0.5rem 0.75rem", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                    <div style={{ color: key === "overlay" ? SCAN : ACCENT, fontWeight: "bold", fontSize: 13, letterSpacing: 0.5 }}>{title}</div>
                    <div style={{ color: "#777", fontSize: 11, fontFamily: "monospace" }}>{desc}</div>
                  </div>
                  {src(images[key]) ? (
                    <img src={src(images[key])} alt={title} style={{ width: "100%", display: "block" }} />
                  ) : (
                    <div style={{ height: 200, display: "flex", alignItems: "center", justifyContent: "center", color: "#555", fontFamily: "monospace", fontSize: 12 }}>
                      {key === "object" || key === "overlay" ? "No object isolated" : "—"}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : !loading ? (
            <div style={{ padding: "3rem 1rem", textAlign: "center", color: "#666", border: "1px dashed rgba(103,58,183,0.3)", borderRadius: 10 }}>
              <div style={{ fontSize: "2.5rem", marginBottom: "0.5rem" }}>🔍</div>
              Run diagnostics to see the color frame, depth, segmentation overlay,
              and the isolated object side-by-side.
            </div>
          ) : null}

          {loading && !images && (
            <div style={{ padding: "2rem", display: "flex", justifyContent: "center" }}>
              <LoadingSpinner />
            </div>
          )}

          {/* ---- Streaming self-test ---- */}
          <div style={{ marginTop: "2rem", borderTop: "1px solid rgba(255,255,255,0.08)", paddingTop: "1.25rem" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem", marginBottom: "0.75rem" }}>
              <div>
                <h3 style={{ margin: 0, color: SCAN, letterSpacing: 1 }}>Full-Detail Streaming Test</h3>
                <p style={{ margin: "0.25rem 0 0", color: "#8a8a8a", fontSize: "0.85rem", fontFamily: "monospace" }}>
                  Runs the object stream and traces chunks received, points assembled, bytes, and timing.
                </p>
              </div>
              <button
                onClick={runStreamTest}
                disabled={streamRunning}
                style={{
                  padding: "0.6rem 1.4rem",
                  borderRadius: 8,
                  border: `2px solid ${SCAN}`,
                  background: "rgba(0,229,255,0.12)",
                  color: SCAN,
                  fontWeight: "bold",
                  textTransform: "uppercase",
                  cursor: streamRunning ? "wait" : "pointer",
                }}
              >
                {streamRunning ? "Streaming…" : "▶ Test Streaming"}
              </button>
            </div>

            {streamLog.length > 0 ? (
              <pre
                style={{
                  background: "#0a0a12",
                  border: "1px solid rgba(0,229,255,0.25)",
                  borderRadius: 8,
                  padding: "0.9rem 1rem",
                  color: "#c7f7ff",
                  fontFamily: "ui-monospace, monospace",
                  fontSize: 12,
                  lineHeight: 1.5,
                  maxHeight: 320,
                  overflow: "auto",
                  margin: 0,
                  whiteSpace: "pre-wrap",
                }}
              >
                {streamLog.join("\n")}
              </pre>
            ) : (
              <div style={{ padding: "1.5rem 1rem", textAlign: "center", color: "#666", border: "1px dashed rgba(0,229,255,0.25)", borderRadius: 8, fontFamily: "monospace", fontSize: 13 }}>
                Capture an object first (3D Capture tab), then run this to trace the streaming path.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  ) : (
    <LoadingSpinner />
  );
};

export default Diagnostics;
